"use client";
import classNames from "classnames";
import styles from "./styles.module.scss";
import { useCallback, useMemo, useEffect, useRef, useState, use } from "react";

import IconArrowChevronLeft from "@/components/Icon/IconArrowChevronLeft.svg";
import IconArrowChevronRight from "@/components/Icon/IconArrowChevronRight.svg";

import ProductItem from "@/components/ProductLayout/ProductItem";
import Image from "next/image";
import Link from "next/link";
import { capitalize } from "@/utils/common";

export default function Discover({ brand, department, collection, category, similarProductsPromise }) {
  const similarProducts = use(similarProductsPromise);
  const relatedProductsRef = useRef(null);
  const relatedProductRef = useRef(null);

  const [currentRelatedProductPage, setCurrentRelatedProductPage] = useState(1);
  const [relatedProductPageSize, setRelatedProductPageSize] = useState(null);
  const [totalRetatedProductPage, setTotalRetatedProductPage] = useState(0);

  const goToRelatedProductPage = useCallback(
    index => {
      if (index <= 0 || index > totalRetatedProductPage || !relatedProductRef.current) return;
      const width = relatedProductRef.current?.getBoundingClientRect().width;
      var left = (width + 32) * relatedProductPageSize;
      if (index < currentRelatedProductPage) left = -left;
      relatedProductsRef.current.scrollBy({ left: left, behavior: "smooth" });
      setCurrentRelatedProductPage(index);
    },
    [currentRelatedProductPage, relatedProductPageSize, totalRetatedProductPage]
  );

  useEffect(() => {
    const handleResize = () => {
      if (!relatedProductRef.current) return;
      const container = relatedProductsRef.current.getBoundingClientRect().width;
      const item = relatedProductRef.current.getBoundingClientRect().width;
      const pageSize = parseInt(container / item);
      const pageCount = Math.ceil(similarProducts.length / pageSize);
      setTotalRetatedProductPage(pageCount);
      setRelatedProductPageSize(pageSize);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const relatedCategories = useMemo(() => {
    const categories = [];
    if (brand) {
      categories.push({
        name: brand.name,
        url: `/brands/${brand.slug}`,
      });
    }
    if (department && collection) {
      categories.push({
        name: collection.name,
        url: `/${department.slug}/${collection.slug}`,
      });
    }
    if (department && collection && category) {
      categories.push({
        name: category.name,
        url: `/${department.slug}/${collection.slug}/${category.slug}`,
      });
    }
    if (brand && collection) {
      const brandCollectionSlug = `${brand.name}-${collection.slug}`;
      categories.push({
        name: capitalize(brandCollectionSlug),
        url: `/brands/${brand.slug}/${brandCollectionSlug}`,
      });
    }
    return categories;
  }, [brand, category, collection, department]);

  return (
    <>
      <div className=" pb-7">
        <p className="text-[21px] font-bold">Related categories</p>
        <div className="flex flex-wrap gap-2 mt-4 md:mt-7">
          {relatedCategories.map((item, index) => (
            <Link key={`related-category-${index}`} href={item.url}>
              <div className="border-[1px] border-grey-200 py-[5px] px-6 text-sm font-bold rounded-[18px]">
                {item.name}
              </div>
            </Link>
          ))}
        </div>
      </div>
      <div className="relative pb-8 md:pb-0 border-0 md:border-t-[1px] border-grey-200">
        <div className="flex justify-between pb-0 lg:pb-[22px] pt-3 md:pt-8 lg:pt-[22px]">
          <p className="text-[21px] font-bold">You may also like...</p>
          <div className="flex gap-2 ml-auto absolute md:static bottom-0 right-0">
            <div
              className="w-8 h-8 border-[1px] border-black flex rounded-full cursor-pointer"
              onClick={() => goToRelatedProductPage(currentRelatedProductPage - 1)}
            >
              <Image
                src={IconArrowChevronLeft.src}
                width="6"
                height="18"
                alt="IconArrowChevronLeft"
                className="h-3 w-[6px] m-auto"
              />
            </div>
            <div
              className="w-8 h-8 border-[1px] border-black flex rounded-full cursor-pointer"
              onClick={() => goToRelatedProductPage(currentRelatedProductPage + 1)}
            >
              <Image
                src={IconArrowChevronRight.src}
                width="6"
                height="18"
                alt="IconArrowChevronRight"
                className="h-3 w-[6px] m-auto"
              />
            </div>
          </div>
        </div>
        <div
          ref={relatedProductsRef}
          className={classNames("flex gap-4 md:gap-8 overflow-y-auto pb-4 -mx-2 md:mx-0", styles.horizontalScrollBar)}
        >
          {similarProducts &&
            similarProducts.length > 0 &&
            similarProducts.map((product, index) => (
              <div ref={relatedProductRef} key={`product-${product.name}-${index}`}>
                <ProductItem
                  product={product}
                  className="min-w-[180px] md:min-w-[286px] h-full border-b-[1px] border-grey-100 pb-6"
                />
              </div>
            ))}
        </div>
        <div className="hidden md:flex gap-3">
          {Array.from({ length: totalRetatedProductPage }, (_, index) => index + 1).map((product, index) => (
            <div
              key={`dot-${index}`}
              className={classNames(
                "w-2 h-2 border-[1px] border-black rounded-full cursor-pointer",
                index + 1 === currentRelatedProductPage && "bg-black"
              )}
              onClick={() => goToRelatedProductPage(index + 1)}
            ></div>
          ))}
        </div>
      </div>
    </>
  );
}
